@if (loading()) {
  <div class="container-fluid py-6 text-center text-muted">A carregar…</div>
} @else if (error()) {
  <div class="container-fluid py-6 text-center">
    <p class="text-danger">{{ error() }}</p>
    <a class="btn btn-white" routerLink="..">Voltar aos casos críticos</a>
  </div>
} @else if (item(); as c) {
  <div class="container-fluid">
    <app-page-header
      icon="fe-alert-octagon"
      [pretitle]="'Caso crítico #' + c.id"
      [title]="c.target.name"
      [description]="targetLabels[c.target.type] + ' · reportado em ' + (c.reportDate | date: 'dd/MM/yyyy')"
    >
      <div actions>
        <a class="btn btn-outline-light me-2" routerLink="..">Voltar</a>
        @if (auth.can('occurrence:write')) {
          <a class="btn btn-white" [routerLink]="['/', auth.currentTenant(), 'ocorrencias', 'nova']"
             [queryParams]="{ casoCritico: c.id }">
            <i class="fe fe-plus me-1"></i> Abrir ocorrência
          </a>
        }
      </div>
    </app-page-header>
  </div>

  <div class="container-fluid">
    <div class="row">
      <div class="col-12 col-xl-8">
        <!-- O caso -->
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title mb-0">Dados do caso</h4>
            <div class="d-flex gap-2">
              <span class="badge" [class]="severityBadge(c.severityLevel)">{{ c.severityName }}</span>
              <span class="badge" [class]="statusBadge(c.statusCategory)">{{ c.statusName }}</span>
              @if (c.archived) { <span class="badge bg-secondary-soft">Arquivado</span> }
            </div>
          </div>
          <div class="card-body">
            <div class="row g-3 mb-4">
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-calendar"></i>
                  <div><span class="occ-date-label">Data de reporte</span><span class="occ-date-value">{{ c.reportDate | date: 'dd/MM/yyyy' }}</span></div></div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-clock"></i>
                  <div><span class="occ-date-label">Próximo acompanhamento</span><span class="occ-date-value">{{ c.nextFollowUp ? (c.nextFollowUp | date: 'dd/MM/yyyy') : '—' }}</span></div></div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-flag"></i>
                  <div><span class="occ-date-label">Encerrado em</span><span class="occ-date-value">{{ c.closedAt ? (c.closedAt | date: 'dd/MM/yyyy HH:mm') : '—' }}</span></div></div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-inbox"></i>
                  <div><span class="occ-date-label">Origem</span><span class="occ-date-value">{{ c.originName || '—' }}</span></div></div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-help-circle"></i>
                  <div><span class="occ-date-label">Motivo</span><span class="occ-date-value">{{ c.reasonName || '—' }}</span></div></div>
              </div>
              <div class="col-12 col-sm-6 col-lg-4">
                <div class="occ-date"><i class="fe fe-watch"></i>
                  <div><span class="occ-date-label">Prazo de resposta</span><span class="occ-date-value">{{ c.responseDeadlineDays !== null ? c.responseDeadlineDays + ' dias' : '—' }}</span></div></div>
              </div>
            </div>

            <h6 class="text-muted text-uppercase mb-2">Descrição</h6>
            <p class="mb-4" style="white-space: pre-line;">{{ c.description || '—' }}</p>
            <h6 class="text-muted text-uppercase mb-2">Ação corretiva</h6>
            <p class="mb-0" style="white-space: pre-line;">{{ c.correctiveAction || '—' }}</p>
            @if (c.closingNote) {
              <h6 class="text-muted text-uppercase mb-2 mt-4">Nota de encerramento</h6>
              <p class="mb-0" style="white-space: pre-line;">{{ c.closingNote }}</p>
            }
          </div>
        </div>

        <!-- Ocorrências ligadas -->
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title mb-0">Ocorrências ligadas</h4>
          </div>
          @if (c.occurrences.length) {
            <div class="list-group list-group-flush">
              @for (o of c.occurrences; track o.id) {
                <a class="list-group-item list-group-item-action d-flex align-items-center gap-3"
                   [routerLink]="['/', auth.currentTenant(), 'ocorrencias', o.id]">
                  <span class="avatar avatar-sm"><span class="avatar-title fs-lg rounded bg-primary-soft text-primary"><i class="fe fe-alert-triangle"></i></span></span>
                  <div class="flex-grow-1 min-w-0">
                    <span class="fw-bold d-block text-truncate">{{ o.code }} — {{ o.title }}</span>
                    <span class="small text-muted">Criada em {{ o.createdAt | date: 'dd/MM/yyyy HH:mm' }}</span>
                  </div>
                  <span class="badge bg-secondary-soft">{{ stateLabel(o.state) }}</span>
                </a>
              }
            </div>
          } @else {
            <div class="card-body">
              <p class="text-muted mb-0">Ainda não há ocorrências ligadas a este caso. Use “Abrir ocorrência” para registar a primeira.</p>
            </div>
          }
        </div>
      </div>

      <div class="col-12 col-xl-4">
        <!-- Pessoas envolvidas -->
        <div class="card">
          <div class="card-header">
            <h4 class="card-header-title mb-0">Pessoas envolvidas</h4>
          </div>
          <div class="list-group list-group-flush">
            @for (p of c.people; track p.role) {
              <div class="list-group-item">
                <div class="row align-items-center">
                  <div class="col-auto">
                    <span class="avatar avatar-sm"><span class="avatar-title rounded-circle client-avatar">{{ initials(p.name) }}</span></span>
                  </div>
                  <div class="col ms-n2 min-w-0">
                    <h5 class="mb-0 text-truncate">{{ p.name }}</h5>
                    <p class="small text-muted mb-1">{{ roleLabels[p.role] }}</p>
                    @if (p.email) {
                      <a class="small d-block text-truncate" [href]="'mailto:' + p.email"><i class="fe fe-mail me-1"></i>{{ p.email }}</a>
                    }
                    @if (p.phone) {
                      <a class="small d-block" [href]="'tel:' + p.phone"><i class="fe fe-phone me-1"></i>{{ p.phone }}</a>
                    }
                  </div>
                </div>
              </div>
            } @empty {
              <div class="list-group-item text-muted">Sem pessoas associadas.</div>
            }
          </div>
          @if (c.target.type === 'CLIENT') {
            <!-- O cliente não é uma pessoa da lista: aparece à parte, com o quadrado. -->
            <div class="card-footer">
              <div class="d-flex align-items-center gap-3">
                <span class="avatar avatar-sm"><span class="avatar-title rounded bg-info-soft text-info"><i class="fe fe-briefcase"></i></span></span>
                <div class="min-w-0">
                  <h5 class="mb-0 text-truncate">{{ c.target.name }}</h5>
                  <p class="small text-muted mb-1">Cliente@if (c.target.departmentName) { · {{ c.target.departmentName }} }</p>
                  @if (c.target.email) {
                    <a class="small d-block text-truncate" [href]="'mailto:' + c.target.email"><i class="fe fe-mail me-1"></i>{{ c.target.email }}</a>
                  }
                  @if (c.target.phone) {
                    <a class="small d-block" [href]="'tel:' + c.target.phone"><i class="fe fe-phone me-1"></i>{{ c.target.phone }}</a>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  </div>
}
